import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Build } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

export interface NewAuditRequest {
  url?: string;
  buildId?: string;
  projectId?: string;
}

interface NewAuditFormProps {
  projects: Project[];
  /** Опубликованные сайты пользователя — аудит смотрит на живую страницу. */
  builds: Build[];
  isSubmitting: boolean;
  onSubmit: (request: NewAuditRequest) => void;
}

const NONE = '__none__';

/**
 * Адрес без схемы — обычное дело при копировании из адресной строки.
 *
 * Схему дописываем, только если её нет вовсе. Проверять именно `http(s)` нельзя:
 * тогда `file:///etc/passwd` превратился бы в `https://file:///etc/passwd`, то есть
 * запрещённая схема приехала бы на бэкенд под видом обычного хоста.
 */
function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * What to audit, and against what.
 *
 * URL and "my own site" are mutually exclusive by construction rather than by
 * validation: picking a site clears the address field, because the two are the same
 * question asked twice and the backend refuses a request carrying both.
 *
 * The project is optional and does something specific: with it, the AI compares the
 * page's offer against the positioning and personas of that project. Without it, that
 * part of the report is skipped rather than guessed — so the hint says so.
 */
export function NewAuditForm({ projects, builds, isSubmitting, onSubmit }: NewAuditFormProps) {
  const { t } = useTranslation('siteAudit');

  const [url, setUrl] = useState('');
  const [buildId, setBuildId] = useState(NONE);
  const [projectId, setProjectId] = useState(NONE);

  const usingBuild = buildId !== NONE;
  const canSubmit = (usingBuild || url.trim().length > 0) && !isSubmitting;

  function submit() {
    if (!canSubmit) return;
    onSubmit({
      url: usingBuild ? undefined : normalizeUrl(url),
      buildId: usingBuild ? buildId : undefined,
      projectId: projectId === NONE ? undefined : projectId,
    });
  }

  return (
    <form
      className="flex flex-col gap-[16px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] p-[20px]"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-[6px]">
        <Label htmlFor="audit-url" className="text-[14px] text-[color:var(--grey-text,#565858)]">
          {t('form.url.label')}
        </Label>
        <Input
          id="audit-url"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            if (event.target.value) setBuildId(NONE);
          }}
          placeholder={t('form.url.placeholder')}
          maxLength={2000}
          inputMode="url"
          className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
        />
      </div>

      {builds.length > 0 && (
        <div className="flex flex-col gap-[6px]">
          <Label htmlFor="audit-build" className="text-[14px] text-[color:var(--grey-text,#565858)]">
            {t('form.build.label')}
          </Label>
          <Select
            value={buildId}
            onValueChange={(value) => {
              setBuildId(value);
              if (value !== NONE) setUrl('');
            }}
          >
            <SelectTrigger id="audit-build">
              <SelectValue placeholder={t('form.build.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('form.build.placeholder')}</SelectItem>
              {builds.map((build) => (
                <SelectItem key={build.id} value={build.id}>
                  {build.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
            {t('form.build.hint')}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-[6px]">
        <Label htmlFor="audit-project" className="text-[14px] text-[color:var(--grey-text,#565858)]">
          {t('form.project.label')}
        </Label>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger id="audit-project">
            <SelectValue placeholder={t('form.project.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('form.project.placeholder')}</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('form.project.hint')}
        </p>
      </div>

      <Button type="submit" variant="brand" size="brand" disabled={!canSubmit} className="self-start">
        {isSubmitting ? t('form.submitting') : t('form.submit')}
      </Button>
    </form>
  );
}
